import React, { useState } from "react";
import { Box, Button, Menu, MenuItem } from "@mui/material";
import { Theme } from "@mui/material/styles";
import { KeyboardArrowDown } from "@mui/icons-material";
import { HeaderCategory } from "./main-layout.types";
import { subcategoriesService, Subcategory, SubcategoryTreeNode } from "../../services/subcategories";
import { buildCategoryRoute } from "../../utils/categoryRoutes";

interface CategoriesBarProps {
  t: (key: string, options?: Record<string, unknown>) => string;
  theme: Theme;
  category: string;
  categories: HeaderCategory[];
  onCategoryChange: (value: string) => void;
  subcategories?: Subcategory[];
}

const CategoriesBar: React.FC<CategoriesBarProps> = ({
  t,
  theme,
  category,
  categories,
  onCategoryChange,
  subcategories = [],
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [activeCategoryKey, setActiveCategoryKey] = useState<string | null>(null);

  // Build subcategory trees for each header category
  const subcategoryTree = subcategoriesService.buildTree(subcategories);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, catKey: string) => {
    setAnchorEl(event.currentTarget);
    setActiveCategoryKey(catKey);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setActiveCategoryKey(null);
  };

  const handleSubcategoryClick = (categoryKey: string, node: SubcategoryTreeNode) => {
    const segments = subcategoriesService.getPathSegments(
      subcategories,
      node.key,
      categoryKey,
    );
    const route = buildCategoryRoute(categoryKey, segments);
    if (route) onCategoryChange(route);
    handleMenuClose();
  };

  const handleCategoryOnlyClick = (categoryKey: string) => {
    const route = buildCategoryRoute(categoryKey);
    if (route) onCategoryChange(route);
    handleMenuClose();
  };

  const getSubcategoriesForCategory = (catKey: string) => {
    return subcategoryTree.filter((node) => node.category_key === catKey);
  };

  const renderSubcategoryTree = (
    categoryKey: string,
    nodes: SubcategoryTreeNode[],
    depth: number = 0,
  ): React.ReactNode => {
    return nodes.map((node) => (
      <Box key={node.id}>
        <MenuItem
          onClick={() => handleSubcategoryClick(categoryKey, node)}
          sx={{
            pl: 2 + depth * 2,
            fontSize: depth > 0 ? "0.8rem" : "0.875rem",
            fontWeight: depth === 0 ? 600 : 400,
          }}
        >
          {depth > 0 && (
            <Box
              component="span"
              sx={{
                width: 8,
                height: 8,
                mr: 1,
                borderLeft: "2px solid",
                borderBottom: "2px solid",
                borderColor: "rgba(0,212,255,0.3)",
                display: "inline-block",
              }}
            />
          )}
          {node.label_en || node.key}
        </MenuItem>
        {node.children.length > 0 &&
          renderSubcategoryTree(categoryKey, node.children, depth + 1)}
      </Box>
    ));
  };

  return (
    <Box
      sx={{
        backgroundColor:
          theme.palette.mode === "dark"
            ? "rgba(0, 212, 255, 0.05)"
            : "rgba(32, 32, 32, 0.216)",
        width: "100%",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.5,
          py: 1,
          px: { xs: 1.5, sm: 2 },
          overflowX: "auto",
          justifyContent: { xs: "flex-start", md: "center" },
          "&::-webkit-scrollbar": {
            height: 3,
            display: { xs: "block", md: "none" },
          },
          "&::-webkit-scrollbar-thumb": {
            bgcolor: "rgba(255,255,255,0.2)",
            borderRadius: 8,
          },
          "&::-webkit-scrollbar-track": {
            bgcolor: "rgba(255,255,255,0.05)",
          },
        }}
      >
        <Button
          size="small"
          onClick={() => onCategoryChange("")}
          sx={{
            color: category === "" ? "#00d4ff" : theme.palette.text.primary,
            px: { xs: 1.5, sm: 2 },
            py: { xs: 0.25, sm: 0.5 },
            minHeight: { xs: 28, sm: 32 },
            fontSize: { xs: "0.75rem", sm: "0.875rem" },
            borderRadius: "24px",
            border:
              category === ""
                ? "1px solid rgba(0,212,255,0.5)"
                : "1px solid transparent",
            bgcolor: category === "" ? "rgba(0,212,255,0.12)" : "transparent",
            "&:hover": {
              bgcolor: "rgba(0,212,255,0.15)",
              color: "#00d4ff",
            },
            whiteSpace: "nowrap",
          }}
        >
          {
            t("products.allCategories", { defaultValue: "All" }).split(" ")[0]
          }
        </Button>

        {categories.map((cat) => {
          const hasSubcategories = getSubcategoriesForCategory(cat.key).length > 0;

          return (
            <Box key={cat.key}>
              <Button
                size="small"
                onClick={(e) =>
                  hasSubcategories
                    ? handleMenuOpen(e, cat.key)
                    : handleCategoryOnlyClick(cat.key)
                }
                endIcon={hasSubcategories ? <KeyboardArrowDown /> : undefined}
                sx={{
                  color:
                    category === cat.key
                      ? "#00d4ff"
                      : theme.palette.text.primary,
                  px: { xs: 1.5, sm: 2 },
                  py: { xs: 0.25, sm: 0.5 },
                  minHeight: { xs: 28, sm: 32 },
                  fontSize: { xs: "0.75rem", sm: "0.875rem" },
                  borderRadius: "24px",
                  border:
                    category === cat.key
                      ? "1px solid rgba(0,212,255,0.5)"
                      : "1px solid transparent",
                  bgcolor:
                    category === cat.key
                      ? "rgba(0,212,255,0.12)"
                      : "transparent",
                  "&:hover": {
                    bgcolor: "rgba(0,212,255,0.15)",
                    color: "#00d4ff",
                  },
                  whiteSpace: "nowrap",
                }}
              >
                {cat.label}
              </Button>

              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl) && activeCategoryKey === cat.key}
                onClose={handleMenuClose}
                MenuListProps={{ dense: true }}
              >
                <MenuItem
                  onClick={() => handleCategoryOnlyClick(cat.key)}
                  sx={{ fontWeight: 700, color: "primary.main" }}
                >
                  {t("products.allInCategory", { defaultValue: "All in {category}", category: cat.label })}
                </MenuItem>
                <Box sx={{ borderTop: "1px solid rgba(0,0,0,0.08)", my: 0.5 }} />
                {renderSubcategoryTree(cat.key, getSubcategoriesForCategory(cat.key))}
              </Menu>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default CategoriesBar;